/* eslint-disable no-restricted-globals */
import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import './MeetingRoom.css';

const SIGNALING_SERVER = process.env.REACT_APP_SIGNALING_SERVER || 'http://localhost:5000';

function MeetingRoom({ groupId, groupName, user, userData, onClose }) {
  const [isConnected, setIsConnected] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isInLobby, setIsInLobby] = useState(false);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [currentCameraId, setCurrentCameraId] = useState(null);
  const [currentFacingMode, setCurrentFacingMode] = useState('user'); // 'user' = front, 'environment' = back
  const [showCameraMenu, setShowCameraMenu] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [stageMode, setStageMode] = useState(false);
  const [stageUsers, setStageUsers] = useState([]);
  const [isForceMuted, setIsForceMuted] = useState(false);

  const socketRef = useRef(null);   
  const localStreamRef = useRef(null);
  const localVideoRef = useRef(null);
  const peersRef = useRef(new Map());
  const wakeLockRef = useRef(null);

  // Keep screen awake during call
  useEffect(() => {
    const requestWakeLock = async () => {
      if ('wakeLock' in navigator && isConnected) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          console.log('Wake lock acquired');
        } catch (err) {
          console.log('Wake lock error:', err);
        }
      }
    };

    const releaseWakeLock = async () => {
      if (wakeLockRef.current) {
        try {
          await wakeLockRef.current.release();
          wakeLockRef.current = null;
          console.log('Wake lock released');
        } catch (err) {
          console.log('Wake lock release error:', err);
        }
      }
    };

    if (isConnected) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }

    return () => {
      releaseWakeLock();
    };
  }, [isConnected]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('Page hidden - call continues in background');
      } else {
        console.log('Page visible - call active');
        // Reacquire wake lock if needed
        if (isConnected && 'wakeLock' in navigator) {
          navigator.wakeLock.request('screen')
            .then(lock => { wakeLockRef.current = lock; })
            .catch(err => console.log('Wake lock reacquire error:', err));
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isConnected]);

  // Prevent accidental page close during call
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isConnected) {
        e.preventDefault();
        e.returnValue = 'You are currently in a call. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isConnected]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (isConnected) {
        leaveRoom();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // Initialize preview when component mounts
  useEffect(() => {
    const initializePreview = async () => {
      try {
        // Get available cameras
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cameras = devices.filter(device => device.kind === 'videoinput');
        setAvailableCameras(cameras);

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true
        });

        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        // Store current camera ID
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          setCurrentCameraId(videoTrack.getSettings().deviceId);
        }

        // Set initial states
        stream.getAudioTracks()[0].enabled = audioEnabled;
        stream.getVideoTracks()[0].enabled = videoEnabled;
        
        setIsInLobby(true);
      } catch (error) {
        console.error('Error accessing media devices:', error);
        alert('Could not access camera/microphone. Please check permissions.');
      }
    };

    initializePreview();

    return () => {
      // Cleanup preview stream if not connected
      if (!isConnected && localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
        localStreamRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep video element synced with stream
  useEffect(() => {
    if (localVideoRef.current && localStreamRef.current) {
      localVideoRef.current.srcObject = localStreamRef.current;
    }
  }, [isConnected]);

  const joinRoom = async () => {
    setIsConnecting(true);
    try {
      // Use existing stream or get new one
      if (!localStreamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true
        });

        localStreamRef.current = stream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        // Set initial states
        stream.getAudioTracks()[0].enabled = audioEnabled;
        stream.getVideoTracks()[0].enabled = videoEnabled;
      }

      // Connect to signaling server
      socketRef.current = io(SIGNALING_SERVER);

      // Socket event handlers
      socketRef.current.on('connect', () => {
        console.log('Connected to signaling server');
        
        // Join the room
        socketRef.current.emit('join-room', {
          roomId: groupId,
          userId: user.uid,
          userName: userData?.name || user.email.split('@')[0],
          userToken: null // Add FCM token here if you implement it
        });

        setIsConnected(true);
        setIsConnecting(false);
      });

      socketRef.current.on('room-participants', async (participantsList) => {
        console.log('Room participants:', participantsList);
        setParticipants(participantsList);
        
        // Create peer connections for existing participants
        for (const participant of participantsList) {
          if (participant.userId !== user.uid) {
            console.log(`Creating peer connection for existing participant: ${participant.userId}`);
            
            // Check if peer connection already exists
            if (!peersRef.current.has(participant.userId)) {
              await createPeerConnection(participant.userId, true);
            } else {
              console.log(`Peer connection already exists for ${participant.userId}`);
            }
          }
        }
      });

      socketRef.current.on('user-connected', async ({ userId, userName, participants: updatedParticipants }) => {
        console.log(`${userName} connected (userId: ${userId})`);
        setParticipants(updatedParticipants);

        // Create peer connection for new user (only if it's not ourselves)
        if (userId !== user.uid) {
          console.log(`Creating peer connection for new user: ${userId}`);
          
          // Close existing peer connection if any
          const existingPeer = peersRef.current.get(userId);
          if (existingPeer) {
            console.log(`Closing existing peer connection for ${userId}`);
            existingPeer.close();
            peersRef.current.delete(userId);
          }
          
          await createPeerConnection(userId, true);
        } else {
          console.log('Skipping peer connection for self');
        }
      });

      socketRef.current.on('user-disconnected', ({ userId, userName }) => {
        console.log(`${userName} disconnected`);
        
        // Remove peer connection
        const peer = peersRef.current.get(userId);
        if (peer) {
          peer.close();
          peersRef.current.delete(userId);
        }

        setParticipants(prev => prev.filter(p => p.userId !== userId));
      });

      socketRef.current.on('offer', async ({ from, offer }) => {
        console.log(`Received offer from ${from}`);
        const peer = await createPeerConnection(from, false);
        await peer.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await peer.createAnswer();
        await peer.setLocalDescription(answer);
        console.log(`Sending answer to ${from}`);
        socketRef.current.emit('answer', { to: from, answer });
      });

      socketRef.current.on('answer', async ({ from, answer }) => {
        console.log(`Received answer from ${from}`);
        const peer = peersRef.current.get(from);
        if (peer) {
          await peer.setRemoteDescription(new RTCSessionDescription(answer));
          console.log(`Set remote description for ${from}`);
        } else {
          console.warn(`No peer connection found for ${from}`);
        }
      });

      socketRef.current.on('ice-candidate', async ({ from, candidate }) => {
        console.log(`Received ICE candidate from ${from}`);
        const peer = peersRef.current.get(from);
        if (peer && candidate) {
          await peer.addIceCandidate(new RTCIceCandidate(candidate));
          console.log(`Added ICE candidate for ${from}`);
        } else if (!peer) {
          console.warn(`No peer connection found for ${from}`);
        }
      });

      socketRef.current.on('user-audio-toggle', ({ userId, enabled }) => {
        setParticipants(prev => prev.map(p => 
          p.userId === userId ? { ...p, audio: enabled } : p
        ));
      });

      socketRef.current.on('user-video-toggle', ({ userId, enabled }) => {
        setParticipants(prev => prev.map(p => 
          p.userId === userId ? { ...p, video: enabled } : p
        ));
      });

      // Admin control events
      socketRef.current.on('force-mute', ({ adminId }) => {
        console.log('Admin forced mute');
        setIsForceMuted(true);
        if (localStreamRef.current) {
          const audioTrack = localStreamRef.current.getAudioTracks()[0];
          if (audioTrack) {
            audioTrack.enabled = false;
            setAudioEnabled(false);
          }
        }
        alert('An admin has muted you');
      });

      socketRef.current.on('allow-unmute', ({ adminId }) => {
        console.log('Admin allowed unmute');
        setIsForceMuted(false);
        alert('You can now unmute yourself');
      });

      // Stage mode events
      socketRef.current.on('stage-mode-changed', ({ enabled, stageUsers: users }) => {
        setStageMode(enabled);
        setStageUsers(users || []);
      });

      socketRef.current.on('user-added-to-stage', ({ userId }) => {
        setStageUsers(prev => [...prev, userId]);
      });

      socketRef.current.on('user-removed-from-stage', ({ userId }) => {
        setStageUsers(prev => prev.filter(id => id !== userId));
      });

    } catch (error) {
      console.error('Error joining room:', error);
      alert('Could not access camera/microphone. Please check permissions.');
      setIsConnecting(false);
    }
  };

  const createPeerConnection = async (userId, isInitiator) => {
    console.log(`Creating peer connection for ${userId}, initiator: ${isInitiator}`);
    
    const configuration = {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };

    const peer = new RTCPeerConnection(configuration);

    // Add local stream tracks
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log(`Adding ${track.kind} track to peer connection`);
        peer.addTrack(track, localStreamRef.current);
      });
    }

    // Handle incoming tracks
    peer.ontrack = (event) => {
      console.log(`Received ${event.track.kind} track from ${userId}`);
      const remoteVideo = document.getElementById(`video-${userId}`);
      if (remoteVideo) {
        remoteVideo.srcObject = event.streams[0];
        console.log(`Set remote stream for ${userId}`);
      } else {
        console.warn(`Video element not found for ${userId}`);
      }
    };

    // Handle ICE candidates
    peer.onicecandidate = (event) => {
      if (event.candidate) {
        console.log(`Sending ICE candidate to ${userId}`);
        socketRef.current.emit('ice-candidate', {
          to: userId,
          candidate: event.candidate
        });
      }
    };

    // Handle connection state changes
    peer.onconnectionstatechange = () => {
      console.log(`Connection state for ${userId}: ${peer.connectionState}`);
    };

    peer.oniceconnectionstatechange = () => {
      console.log(`ICE connection state for ${userId}: ${peer.iceConnectionState}`);
    };

    peersRef.current.set(userId, peer);

    // If initiator, create and send offer
    if (isInitiator) {
      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      console.log(`Sending offer to ${userId}`);
      socketRef.current.emit('offer', { to: userId, offer });
    }

    return peer;
  };

  const toggleAudio = () => {
    if (isForceMuted) {
      alert('You have been muted by an admin');
      return;
    }
    
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setAudioEnabled(audioTrack.enabled);
        
        if (socketRef.current && isConnected) {
          socketRef.current.emit('toggle-audio', {
            roomId: groupId,
            userId: user.uid,
            enabled: audioTrack.enabled
          });
        }
      }
    }
  };

  const switchCamera = async (deviceId) => {
    try {
      // Stop current video track
      if (localStreamRef.current) {
        const videoTrack = localStreamRef.current.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.stop();
        }
      }

      // Get new stream with selected camera
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId } },
        audio: false
      });

      const newVideoTrack = newStream.getVideoTracks()[0];
      
      // Replace track in local stream
      if (localStreamRef.current) {
        const oldVideoTrack = localStreamRef.current.getVideoTracks()[0];
        localStreamRef.current.removeTrack(oldVideoTrack);
        localStreamRef.current.addTrack(newVideoTrack);
        
        // Update video element
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }

        // Replace track in all peer connections
        peersRef.current.forEach(peer => {
          const sender = peer.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            sender.replaceTrack(newVideoTrack);
          }
        });
      }

      setCurrentCameraId(deviceId);
      setShowCameraMenu(false);
    } catch (error) {
      console.error('Error switching camera:', error);
      alert('Could not switch camera');
    }
  };

  const flipCamera = async () => {
    try {
      // Determine new facing mode
      const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
      
      // Stop current video track
      if (localStreamRef.current) {
        const videoTrack = localStreamRef.current.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.stop();
        }
      }

      // Get new stream with opposite camera
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacingMode },
        audio: false
      });

      const newVideoTrack = newStream.getVideoTracks()[0];
      
      // Replace track in local stream
      if (localStreamRef.current) {
        const oldVideoTrack = localStreamRef.current.getVideoTracks()[0];
        localStreamRef.current.removeTrack(oldVideoTrack);
        localStreamRef.current.addTrack(newVideoTrack);
        
        // Update video element
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStreamRef.current;
        }

        // Replace track in all peer connections
        peersRef.current.forEach(peer => {
          const sender = peer.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            sender.replaceTrack(newVideoTrack);
          }
        });
      }

      setCurrentFacingMode(newFacingMode);
      setCurrentCameraId(newVideoTrack.getSettings().deviceId);
    } catch (error) {
      console.error('Error flipping camera:', error);
      alert('Could not flip camera. Your device may not have multiple cameras.');
    }
  };

  const adminMuteUser = (targetUserId) => {
    if (socketRef.current && userData?.role === 'admin') {
      socketRef.current.emit('admin-mute-user', {
        roomId: groupId,
        targetUserId,
        adminId: user.uid
      });
    }
  };

  const adminUnmuteUser = (targetUserId) => {
    if (socketRef.current && userData?.role === 'admin') {
      socketRef.current.emit('admin-unmute-user', {
        roomId: groupId,
        targetUserId,
        adminId: user.uid
      });
    }
  };

  const toggleStageMode = () => {
    if (userData?.role === 'admin' && socketRef.current) {
      const newStageMode = !stageMode;
      setStageMode(newStageMode);
      
      if (!newStageMode) {
        setStageUsers([]);
      } else {
        // Admin is automatically on stage
        setStageUsers([user.uid]);
      }

      socketRef.current.emit('set-stage-mode', {
        roomId: groupId,
        enabled: newStageMode,
        stageUsers: newStageMode ? [user.uid] : []
      });
    }
  };

  const addToStage = (userId) => {
    if (userData?.role === 'admin' && socketRef.current) {
      setStageUsers(prev => [...prev, userId]);
      socketRef.current.emit('add-to-stage', {
        roomId: groupId,
        userId
      });
    }
  };

  const removeFromStage = (userId) => {
    if (userData?.role === 'admin' && socketRef.current) {
      setStageUsers(prev => prev.filter(id => id !== userId));
      socketRef.current.emit('remove-from-stage', {
        roomId: groupId,
        userId
      });
    }
  };

  const isAdmin = userData?.role === 'admin';
  const isOnStage = stageUsers.includes(user.uid);
  
  // Filter participants based on stage mode
  const displayedParticipants = stageMode 
    ? participants.filter(p => stageUsers.includes(p.userId))
    : participants;

  const toggleVideo = () => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setVideoEnabled(videoTrack.enabled);
        
        if (socketRef.current && isConnected) {
          socketRef.current.emit('toggle-video', {
            roomId: groupId,
            userId: user.uid,
            enabled: videoTrack.enabled
          });
        }
      }
    }
  };

  const leaveRoom = () => {
    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Close all peer connections
    peersRef.current.forEach(peer => peer.close());
    peersRef.current.clear();

    // Disconnect socket
    if (socketRef.current) {
      socketRef.current.emit('leave-room', {
        roomId: groupId,
        userId: user.uid,
        userName: userData?.name || user.email.split('@')[0]
      });
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setIsConnected(false);
    setParticipants([]);
    onClose();
  };

  return (
    <div className="meeting-overlay">
      <div className="meeting-modal">
        <div className="meeting-header">
          <div className="meeting-header-left">
            <h2>{groupName} - Meeting Room</h2>
            {stageMode && <span className="stage-badge">Stage Mode</span>}
          </div>
          <div className="meeting-header-right">
            {isAdmin && isConnected && (
              <button onClick={() => setShowAdminMenu(!showAdminMenu)} className="admin-menu-btn" title="Admin Controls">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </button>
            )}
            <button onClick={leaveRoom} className="close-meeting-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {showAdminMenu && isAdmin && isConnected && (
          <div className="admin-menu">
            <div className="admin-menu-header">
              <h3>Admin Controls</h3>
              <button onClick={() => setShowAdminMenu(false)} className="close-admin-menu">×</button>
            </div>
            <div className="admin-menu-content">
              <div className="admin-control-item">
                <label>
                  <input 
                    type="checkbox" 
                    checked={stageMode} 
                    onChange={toggleStageMode}
                  />
                  <span>Stage Mode</span>
                </label>
                <p className="admin-control-desc">Only show selected participants</p>
              </div>
              
              <div className="participants-control">
                <h4>Participants</h4>
                {participants.filter(p => p.userId !== user.uid).map(participant => (
                  <div key={participant.userId} className="participant-control-item">
                    <span className="participant-control-name">{participant.userName}</span>
                    <div className="participant-control-actions">
                      {participant.audio ? (
                        <button 
                          onClick={() => adminMuteUser(participant.userId)}
                          className="control-action-btn mute"
                          title="Mute"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                            <line x1="4" y1="4" x2="20" y2="20" strokeWidth={2} />
                          </svg>
                        </button>
                      ) : (
                        <button 
                          onClick={() => adminUnmuteUser(participant.userId)}
                          className="control-action-btn unmute"
                          title="Allow Unmute"
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                          </svg>
                        </button>
                      )}
                      
                      {stageMode && (
                        stageUsers.includes(participant.userId) ? (
                          <button 
                            onClick={() => removeFromStage(participant.userId)}
                            className="control-action-btn remove-stage"
                            title="Remove from Stage"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        ) : (
                          <button 
                            onClick={() => addToStage(participant.userId)}
                            className="control-action-btn add-stage"
                            title="Add to Stage"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!isConnected ? (
          <div className="meeting-lobby">
            {!isInLobby ? (
              <div className="lobby-loading">
                <div className="spinner"></div>
                <p>Initializing camera and microphone...</p>
              </div>
            ) : (
              <>
                <div className="lobby-preview">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="preview-video"
                  />
                  {!videoEnabled && (
                    <div className="video-off-overlay">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        <line x1="4" y1="4" x2="20" y2="20" strokeWidth={2} />
                      </svg>
                      <p>Camera Off</p>
                    </div>
                  )}
                  <div className="preview-controls">
                    <button onClick={toggleAudio} className={`control-btn ${!audioEnabled ? 'disabled' : ''}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        {audioEnabled ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        )}
                      </svg>
                    </button>
                    <button onClick={toggleVideo} className={`control-btn ${!videoEnabled ? 'disabled' : ''}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        {videoEnabled ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        )}
                      </svg>
                    </button>
                  </div>
                </div>
                <button 
                  onClick={joinRoom} 
                  className="join-meeting-btn"
                  disabled={isConnecting}
                >
                  {isConnecting ? 'Connecting...' : 'Join Meeting'}
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="meeting-grid">
              {/* Local video */}
              {(!stageMode || isOnStage) && (
                <div className={`video-container local ${currentFacingMode === 'environment' ? 'back-camera' : ''}`}>
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className="participant-video"
                  />
                  <div className="video-label">
                    <span>{userData?.name || 'You'}</span>
                    {!audioEnabled && <span className="muted-icon">🔇</span>}
                    {stageMode && <span className="stage-icon">🎭</span>}
                  </div>
                  {availableCameras.length > 1 && (
                    <div className="video-controls-overlay">
                      <button 
                        onClick={flipCamera}
                        className="flip-camera-btn"
                        title="Flip Camera"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                      <button 
                        onClick={() => setShowCameraMenu(!showCameraMenu)}
                        className="switch-camera-btn"
                        title="Select Camera"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                      {showCameraMenu && (
                        <div className="camera-menu">
                          {availableCameras.map(camera => (
                            <button
                              key={camera.deviceId}
                              onClick={() => switchCamera(camera.deviceId)}
                              className={`camera-menu-item ${currentCameraId === camera.deviceId ? 'active' : ''}`}
                            >
                              {camera.label || `Camera ${availableCameras.indexOf(camera) + 1}`}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Remote videos */}
              {displayedParticipants
                .filter(p => p.userId !== user.uid)
                .map(participant => (
                  <div key={participant.userId} className="video-container">
                    <video
                      id={`video-${participant.userId}`}
                      autoPlay
                      playsInline
                      className="participant-video"
                    />
                    <div className="video-label">
                      <span>{participant.userName}</span>
                      {!participant.audio && <span className="muted-icon">🔇</span>}
                      {stageMode && stageUsers.includes(participant.userId) && <span className="stage-icon">🎭</span>}
                    </div>
                  </div>
                ))}
            </div>

            <div className="meeting-controls">
              <button 
                onClick={toggleAudio} 
                className={`control-btn ${!audioEnabled ? 'disabled' : ''}`}
                title={audioEnabled ? 'Mute' : 'Unmute'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  {audioEnabled ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  ) : (
                    <>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      <line x1="4" y1="4" x2="20" y2="20" strokeWidth={2} />
                    </>
                  )}
                </svg>
              </button>

              <button 
                onClick={toggleVideo} 
                className={`control-btn ${!videoEnabled ? 'disabled' : ''}`}
                title={videoEnabled ? 'Stop Video' : 'Start Video'}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  {videoEnabled ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  ) : (
                    <>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      <line x1="4" y1="4" x2="20" y2="20" strokeWidth={2} />
                    </>
                  )}
                </svg>
              </button>

              <button onClick={leaveRoom} className="control-btn leave-btn" title="Leave Meeting">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
                </svg>
              </button>

              <div className="participants-count">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span>
                  {stageMode 
                    ? `${displayedParticipants.length} on stage / ${participants.length} total`
                    : participants.length
                  }
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MeetingRoom;
